import React, { FC, useRef, ChangeEvent, useState } from 'react'
import UploadIcon from 'transfix-icons/build/UploadIcon'
import { makeStyles, Button, Box } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import uuid from 'uuid'
import { IFile, IUploadComponentProps, TFileType } from 'apps/fleet-planner/types'
import { filePromises } from 'utils/fileUploadHelper'

interface IUploadInputProps extends IUploadComponentProps {}

const DEFAULT_INPUT_PROPS: IUploadInputProps['InputProps'] = {
  accept: '.pdf, .jpg, .jpeg, .png, .doc, .docx, .tiff, .heic',
  multiple: true,
}

const DEFAULT_BUTTON_PROPS: IUploadInputProps['ButtonProps'] = {
  size: 'small',
  startIcon: <UploadIcon />,
}

const useUploadInputOverflowMenuStyles = makeStyles({
  input: {
    display: 'none',
  },
  label: {
    display: 'inline-block',
  },
  button: {
    transition: 'none',
    '& span, & .btn-icon-label': {
      lineHeight: 'inherit',
      fontSize: 'inherit',
      color: 'inherit',
      marginTop: 0,
      marginBottom: 0,
    },
    '&:focus': {
      boxShadow: `0 0 0 3px ${brandColors.skyBlue3}`,
      backgroundColor: brandColors.skyBlue7,
      transition: 'none',
    },
  },
})

/**
 * Helper component to show a button for uploading files
 */
export const UploadInputOverflowMenu: FC<IUploadInputProps> = ({
  disabled = false,
  InputProps = {},
  ButtonProps = {},
  onUpload,
}) => {
  const id = useRef(uuid())
  const buttonProps = { ...DEFAULT_BUTTON_PROPS, ...ButtonProps }
  const inputProps = { ...DEFAULT_INPUT_PROPS, ...InputProps }
  const classes = useUploadInputOverflowMenuStyles()
  const scaleTicketRef = useRef<HTMLSpanElement>(null)
  const otherDocumentRef = useRef<HTMLSpanElement>(null)

  const [fileType, setFileType] = useState<TFileType>('private')

  const changeHandler = async (e: ChangeEvent<HTMLInputElement>) => {
    const { target } = e || {}
    const { files } = target || {}

    if (files) {
      const fileList = Array.from(files)
      const packageFiles = await Promise.all(filePromises(fileList))

      const pkgFiles = packageFiles.map(pkg => {
        const pkgFile = pkg as IFile
        pkgFile.uuid = uuid()
        return pkgFile
      })

      scaleTicketRef?.current?.blur()
      otherDocumentRef?.current?.blur()

      onUpload(pkgFiles, fileType)
    }
  }

  return (
    <>
      <input
        {...inputProps}
        className={classes.input}
        id={id.current}
        type='file'
        onChange={changeHandler}
      />
      <Box mb={2}>
        <label htmlFor={id.current} className={classes.label}>
          <Button
            {...buttonProps}
            disabled={disabled}
            className={classes.button}
            component='span'
            onClick={() => {
              setFileType('scale_ticket')
            }}
            ref={scaleTicketRef}>
            Upload Scale...
          </Button>
        </label>
      </Box>
      <Box>
        <label htmlFor={id.current} className={classes.label}>
          <Button
            {...buttonProps}
            disabled={disabled}
            className={classes.button}
            component='span'
            onClick={() => {
              setFileType('misc')
            }}
            ref={otherDocumentRef}>
            Upload Other...
          </Button>
        </label>
      </Box>
    </>
  )
}
